import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssessmentId,
  getCoachSharedProfile,
  saveCoachSharedProfile,
} from "../../../services/rest/kairaAssessments";
import {
  computeBmi,
  FIELD_ALLERGIES,
  FIELD_HEALTH_GOAL,
  FIELD_MEDS,
  GATE_COMORBID_IDS,
  GATE_GLP1,
  selectedComorbidityIds,
  isAnswered,
  isSectionSkipped,
  isSharedProfileSection,
  isSkippedAnswer,
  questionIsVisible,
  sectionIsVisible,
} from "./showIf";
import { toDisplayString } from "../CoachAssessment/QuestionField";

export type SharedLogEntry = {
  coach?: string;
  role?: string;
  date?: string;
  value?: string;
  at?: string;
};

export type SharedLogMap = Record<string, SharedLogEntry[]>;

type LoadedAssessment = {
  assessmentId: AssessmentId;
  config: any;
  sessionId: string | null;
  answers: Record<string, unknown>;
  sharedLog: SharedLogMap;
  published: boolean;
  action: string;
};

const mergeSharedLogs = (...logs: (SharedLogMap | null | undefined)[]) => {
  const out: SharedLogMap = {};
  logs.forEach((log) => {
    if (!log || typeof log !== "object") return;
    Object.entries(log).forEach(([key, entries]) => {
      if (!Array.isArray(entries) || !entries.length) return;
      const merged = [...(out[key] || [])];
      entries.forEach((entry) => {
        if (!entry) return;
        const duplicate = merged.some((item) =>
          item.at && entry.at
            ? item.at === entry.at
            : item.value === entry.value &&
              item.coach === entry.coach &&
              item.date === entry.date,
        );
        if (!duplicate) merged.push(entry);
      });
      merged.sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
      out[key] = merged.slice(-20);
    });
  });
  return out;
};

/** Diet + exercise shared-profile sections merged for one patient. */
export const useSharedProfile = (
  patientId: string | undefined,
  options: { enabled?: boolean } = {},
) => {
  const enabled = options.enabled !== false;
  const [diet, setDiet] = useState<LoadedAssessment | null>(null);
  const [exercise, setExercise] = useState<LoadedAssessment | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [sharedLog, setSharedLog] = useState<SharedLogMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const backupRef = useRef<Record<string, unknown>>({});
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const loadGenRef = useRef(0);

  useEffect(() => {
    if (!enabled) setLoading(false);
  }, [enabled]);

  const ownerByQuestion = useMemo(() => {
    const map: Record<string, AssessmentId> = {};
    const add = (loaded: LoadedAssessment | null) => {
      (loaded?.config?.questions || []).forEach((question: any) => {
        if (question?.id) map[question.id] = loaded!.assessmentId;
      });
    };
    add(diet);
    add(exercise);
    return map;
  }, [diet, exercise]);

  const load = useCallback(async () => {
    if (!patientId || !enabled) return;
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await getCoachSharedProfile(patientId);
      if (gen !== loadGenRef.current) return;
      const dietLoaded = data?.diet || null;
      const exerciseLoaded = data?.exercise || null;
      setDiet(dietLoaded);
      setExercise(exerciseLoaded);
      const merged = {
        ...(exerciseLoaded?.answers || {}),
        ...(dietLoaded?.answers || {}),
      };
      setAnswers(merged);
      setSharedLog(
        mergeSharedLogs(exerciseLoaded?.sharedLog, dietLoaded?.sharedLog),
      );
      setDirty(false);
      setEditing(false);
    } catch (err: any) {
      if (gen !== loadGenRef.current) return;
      setError(err?.message || "Failed to load shared profile");
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [enabled, patientId]);

  useEffect(() => {
    load();
    return () => {
      loadGenRef.current += 1;
    };
  }, [load]);

  const sharedSections = useMemo(() => {
    const dietSecs = (diet?.config?.sections || [])
      .filter(isSharedProfileSection)
      .map((section: any) => ({ ...section, assessmentId: "diet" as AssessmentId }));
    const exSecs = (exercise?.config?.sections || [])
      .filter(isSharedProfileSection)
      .map((section: any) => ({
        ...section,
        assessmentId: "exercise" as AssessmentId,
      }));
    const SHARED_ORDER = [
      "sh_nutrition_goals",
      "ex_preference",
      "ex_barriers",
      "ex_pain",
      "ex_goals",
      "sh_symptoms",
      "sh_anthro",
      "sh_medical_history",
      "sh_glp1",
      "sh_lifestyle_diet",
      "sh_lifestyle_exercise",
      "sh_comorbidities",
    ];
    const orderIndex = (id: string) => {
      const idx = SHARED_ORDER.indexOf(id);
      return idx === -1 ? SHARED_ORDER.length : idx;
    };
    return [...exSecs, ...dietSecs]
      .filter((section) => {
        if (sectionIsVisible(section, answers)) return true;
        // Keep skipped sections visible for coaches even if showIf would hide them.
        const qs =
          (section.assessmentId === "exercise"
            ? exercise?.config?.questions
            : diet?.config?.questions) || [];
        const sectionQs = qs.filter((q: any) => q.sectionId === section.id);
        return isSectionSkipped(sectionQs, answers, {
          sectionSkippable:
            section?.skippable === true ||
            (sectionQs.length > 0 &&
              sectionQs.every((q: any) => q.required !== true)),
          sessionDone:
            (section.assessmentId === "exercise"
              ? exercise?.action
              : diet?.action) === "preview" ||
            (section.assessmentId === "exercise"
              ? exercise?.action
              : diet?.action) === "published",
          patientFill: true,
        });
      })
      .sort(
        (a, b) =>
          orderIndex(a.id) - orderIndex(b.id) ||
          String(a.name || "").localeCompare(String(b.name || "")),
      );
  }, [answers, diet, exercise]);

  const questionsBySection = useMemo(() => {
    const map: Record<string, any[]> = {};
    const add = (loaded: LoadedAssessment | null) => {
      const allQs = loaded?.config?.questions || [];
      const skippedSectionIds = new Set(
        (loaded?.config?.sections || [])
          .filter((section: any) => isSharedProfileSection(section))
          .filter((section: any) => {
            const sectionQs = allQs.filter(
              (q: any) => q.sectionId === section.id,
            );
            return isSectionSkipped(sectionQs, answers, {
              sectionSkippable:
                section?.skippable === true ||
                (sectionQs.length > 0 &&
                  sectionQs.every((q: any) => q.required !== true)),
              sessionDone:
                loaded?.action === "preview" || loaded?.action === "published",
              patientFill: true,
            });
          })
          .map((section: any) => section.id),
      );
      allQs.forEach((question: any) => {
        const forceShow =
          isSkippedAnswer(answers[question.id]) ||
          skippedSectionIds.has(question.sectionId);
        if (!forceShow && !questionIsVisible(question, answers)) return;
        if (!map[question.sectionId]) map[question.sectionId] = [];
        map[question.sectionId].push(question);
      });
    };
    add(diet);
    add(exercise);
    return map;
  }, [answers, diet, exercise]);

  const sessionDone =
    diet?.action === "preview" ||
    diet?.action === "published" ||
    exercise?.action === "preview" ||
    exercise?.action === "published";

  const sectionSkippedFlag = useCallback(
    (section: any) => {
      const qs =
        (section?.assessmentId === "exercise"
          ? exercise?.config?.questions
          : diet?.config?.questions) || [];
      const sectionQs = qs.filter((q: any) => q.sectionId === section.id);
      return isSectionSkipped(sectionQs, answers, {
        sectionSkippable:
          section?.skippable === true ||
          (sectionQs.length > 0 &&
            sectionQs.every((q: any) => q.required !== true)),
        sessionDone,
        patientFill: true,
      });
    },
    [answers, diet, exercise, sessionDone],
  );

  const comorbidOptions = useMemo(() => {
    const question = (diet?.config?.questions || []).find((item: any) =>
      GATE_COMORBID_IDS.includes(String(item.id)),
    );
    return question?.options || [];
  }, [diet]);

  const setAnswer = useCallback(
    (questionId: string, value: unknown) => {
      if (!editing) return;
      setAnswers((prev) => {
        const next = { ...prev, [questionId]: value };
        if (questionId === "weight" || questionId === "height") {
          const bmi = computeBmi(
            questionId === "weight" ? value : next.weight,
            questionId === "height" ? value : next.height,
          );
          if (bmi != null) next.bmi = bmi;
        }
        return next;
      });
      setDirty(true);
      setSaveNote("Unsaved changes");
    },
    [editing],
  );

  const splitByAssessment = useCallback(
    (merged: Record<string, unknown>) => {
      const dietAnswers: Record<string, unknown> = { ...(diet?.answers || {}) };
      const exerciseAnswers: Record<string, unknown> = {
        ...(exercise?.answers || {}),
      };
      Object.entries(merged).forEach(([questionId, value]) => {
        const owner = ownerByQuestion[questionId];
        if (owner === "exercise") exerciseAnswers[questionId] = value;
        else dietAnswers[questionId] = value;
      });
      return { dietAnswers, exerciseAnswers };
    },
    [diet, exercise, ownerByQuestion],
  );

  const persist = useCallback(
    async (merged?: Record<string, unknown>) => {
      if (!patientId) return false;
      const payload = merged ?? answersRef.current;
      setSaving(true);
      try {
        const { dietAnswers, exerciseAnswers } = splitByAssessment(payload);
        const saved = await saveCoachSharedProfile(patientId, payload);
        const dietSaved = saved?.diet;
        const exerciseSaved = saved?.exercise;
        setDiet((prev) =>
          prev
            ? {
                ...prev,
                sessionId: dietSaved?.sessionId || prev.sessionId,
                answers: dietAnswers,
                sharedLog: mergeSharedLogs(
                  prev.sharedLog,
                  dietSaved?.sharedLog,
                ),
                published: false,
              }
            : prev,
        );
        setExercise((prev) =>
          prev
            ? {
                ...prev,
                sessionId: exerciseSaved?.sessionId || prev.sessionId,
                answers: exerciseAnswers,
                sharedLog: mergeSharedLogs(
                  prev.sharedLog,
                  exerciseSaved?.sharedLog,
                ),
                published: false,
              }
            : prev,
        );
        setSharedLog((prev) =>
          mergeSharedLogs(
            prev,
            dietSaved?.sharedLog,
            exerciseSaved?.sharedLog,
          ),
        );
        setDirty(false);
        setSaveNote("Saved");
        return true;
      } catch (err: any) {
        setSaveNote(err?.message || "Save failed");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [diet, exercise, patientId, splitByAssessment],
  );

  const saveCondition = useCallback(
    async (partial: Record<string, unknown>) => {
      const next = { ...answersRef.current, ...partial };
      setAnswers(next);
      answersRef.current = next;
      const ok = await persist(next);
      if (ok) setEditing(false);
      return ok;
    },
    [persist],
  );

  const startEdit = useCallback(() => {
    backupRef.current = { ...answersRef.current };
    setEditing(true);
    setSaveNote("");
  }, []);

  const cancelEdit = useCallback(() => {
    setAnswers(backupRef.current);
    setEditing(false);
    setDirty(false);
    setSaveNote("");
  }, []);

  const saveEdit = useCallback(async () => {
    const ok = await persist();
    if (ok) setEditing(false);
    return ok;
  }, [persist]);

  const sectionAnswered = useCallback(
    (sectionId: string) => {
      const questions = questionsBySection[sectionId] || [];
      let answered = 0;
      questions.forEach((question: any) => {
        if (isAnswered(answers[question.id])) answered += 1;
      });
      return { answered, total: questions.length };
    },
    [answers, questionsBySection],
  );

  return {
    loading,
    error,
    saving,
    saveNote,
    editing,
    dirty,
    answers,
    sharedLog,
    sharedSections,
    questionsBySection,
    comorbidOptions,
    setAnswer,
    startEdit,
    cancelEdit,
    saveEdit,
    saveCondition,
    sectionAnswered,
    sectionSkipped: sectionSkippedFlag,
    glp1: answers[GATE_GLP1] ?? null,
    comorbidities: selectedComorbidityIds(answers),
    healthGoal: toDisplayString(answers[FIELD_HEALTH_GOAL]),
    medications: toDisplayString(answers[FIELD_MEDS]),
    allergies: toDisplayString(answers[FIELD_ALLERGIES]),
  };
};
